import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { BirthDatePicker } from '@/components/organisms/customer/_detail/birth-date-picker'
import { genders } from '@/libs/genders'
import { occupationsWithOthers } from '@/libs/occupations'
import { countryList } from '@/libs/nationality'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import { isEmployee } from '@/models/self-checkin/accommodation-info'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { Select } from '@/components/atoms/select'
import { Select as AntSelect } from 'antd'
import { InputField } from '@/components/molecules/input-field'
import { UpdateGuestParams } from '@/models/guest-input'
import { DeleteIcon } from '../settings/icon/delete-icon'

type Props = {
  index: number
  onDelete: () => void
}

export const AccompanyForm: React.FC<Props> = props => {
  const { control } = useFormContext<UpdateGuestParams>()
  const { t } = useTranslation()
  const watchField = useWatch({ control, name: `accompanyList.${props.index}` })
  const [isOpenedDetail, setIsOpenedDetail] = useState(false)
  const isOtherOccupation = () => watchField.occupation === 'その他'
  const isOtherNationality = () => watchField.nationality && watchField.nationality !== 'JPN'

  return (
    <div css={formStyle}>
      <div>
        <div className="flex-input-block" style={{ gap: 8 }}>
          <div
            onClick={() => setIsOpenedDetail(!isOpenedDetail)}
            style={{ marginBottom: 16, alignSelf: 'center', fontSize: 12, fontWeight: 'bold' }}
          >
            <img
              css={css({ transform: `rotate(${isOpenedDetail ? 180 : 0}deg)` })}
              src={require('@/static/images/arrow_down_nospace.svg')}
            />
          </div>
          <div className="flex-input-block">
            <Controller
              name={`accompanyList.${props.index}.lastName`}
              control={control}
              render={({ field }) => (
                <InputField
                  type="text"
                  fieldWidth="fit-content"
                  width="159px"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('Doe')}
                />
              )}
            />
            <Controller
              name={`accompanyList.${props.index}.firstName`}
              control={control}
              render={({ field }) => (
                <InputField
                  type="text"
                  fieldWidth="fit-content"
                  width="159px"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder={t('John')}
                />
              )}
            />
          </div>

          <div css={css({ cursor: 'pointer', marginBottom: 16, alignSelf: 'center', img: { height: 8.5 } })}>
            <DeleteIcon onClick={() => props.onDelete()} />
          </div>
        </div>
      </div>

      {isOpenedDetail && (
        <div className="detail-form">
          <div>
            <p css={guestInfoLabelStyle}>{t('Residence')}</p>
            <Controller
              name={`accompanyList.${props.index}.nationality`}
              control={control}
              render={({ field }) => (
                <RadioGroupField
                  name={field.name}
                  value={isOtherNationality() ? 'NON' : field.value}
                  onChange={field.onChange}
                  items={[
                    { value: 'JPN', label: t('Japanese nationality') },
                    { value: 'NON', label: t('Foreign nationality') },
                  ]}
                  style={{ radioSize: 18, labelMargin: 8 }}
                  groupCss={{ marginBottom: 16 }}
                  itemWrapperCss={radioButtonWrapperStyle}
                  itemCss={radioButtonStyle}
                />
              )}
            />
            {isOtherNationality() && (
              <Controller
                name={`accompanyList.${props.index}.nationality`}
                control={control}
                render={({ field }) => (
                  <Select value={watchField.nationality === 'NON' ? '' : field.value} onChange={field.onChange}>
                    <AntSelect.Option value="">{t('Select country')}</AntSelect.Option>
                    {countryList('ja', false).map(country => (
                      <AntSelect.Option value={country.value} key={country.value}>
                        {country.name}
                      </AntSelect.Option>
                    ))}
                  </Select>
                )}
              />
            )}
          </div>

          <div>
            <p css={guestInfoLabelStyle}>{t('Furigana')}</p>
            <div className="flex-input-block">
              <Controller
                name={`accompanyList.${props.index}.lastNameKana`}
                control={control}
                render={({ field }) => (
                  <InputField
                    type="text"
                    fieldWidth="fit-content"
                    width="159px"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('Surname')}
                  />
                )}
              />
              <Controller
                name={`accompanyList.${props.index}.firstNameKana`}
                control={control}
                render={({ field }) => (
                  <InputField
                    type="text"
                    fieldWidth="fit-content"
                    width="159px"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder={t('May')}
                  />
                )}
              />
            </div>
          </div>

          <div>
            <p css={guestInfoLabelStyle}>{t('Date of birth')}</p>
            <Controller
              name={`accompanyList.${props.index}.birthDate`}
              control={control}
              render={({ field }) => (
                <div style={{ marginBottom: 16 }}>
                  <BirthDatePicker value={field.value} placeholder={t('Please select')} onChange={field.onChange} />
                </div>
              )}
            />
          </div>

          <div>
            <p css={guestInfoLabelStyle}>{t('Sex')}</p>
            <Controller
              name={`accompanyList.${props.index}.gender`}
              control={control}
              render={({ field }) => (
                <RadioGroupField
                  value={field.value}
                  name={field.name}
                  items={genders}
                  style={{ radioSize: 18, labelMargin: 8 }}
                  groupCss={{ marginBottom: 16 }}
                  itemWrapperCss={radioButtonWrapperStyle}
                  itemCss={radioButtonStyle}
                  onChange={field.onChange}
                />
              )}
            />
          </div>

          <div>
            <p css={guestInfoLabelStyle}>{t('Address')}</p>
            <Controller
              name={`accompanyList.${props.index}.address`}
              control={control}
              render={({ field }) => (
                <InputField type="text" value={field.value} onChange={field.onChange} placeholder={t('1-1 Chiyoda Chiyoda-ku')} />
              )}
            />
          </div>
          <div>
            <p css={guestInfoLabelStyle}>{t('Phone number')}</p>
            <Controller
              name={`accompanyList.${props.index}.telephone`}
              control={control}
              rules={{
                pattern: { value: /^\d+$/, message: t('Please enter numbers only, without symbols or spaces') },
              }}
              render={({ field, fieldState: { error } }) => (
                <InputField type="text" value={field.value} onChange={field.onChange} placeholder="00000000000" error={error?.message} />
              )}
            />
          </div>
          <div>
            <p css={guestInfoLabelStyle}>{t('Email address')}</p>
            <Controller
              name={`accompanyList.${props.index}.email`}
              control={control}
              render={({ field }) => (
                <InputField type="text" value={field.value} onChange={field.onChange} placeholder="xxx@cuicin.co.jp" />
              )}
            />
          </div>
          <div>
            <p css={guestInfoLabelStyle}>{t('Occupation')}</p>
            <Controller
              name={`accompanyList.${props.index}.occupation`}
              control={control}
              render={({ field }) => (
                <Select value={field.value} onChange={field.onChange}>
                  <AntSelect.Option value="">{t('Please select')}</AntSelect.Option>
                  {occupationsWithOthers.map(occupation => (
                    <AntSelect.Option value={t(occupation.label)} key={occupation.value}>
                      {t(occupation.label)}
                    </AntSelect.Option>
                  ))}
                </Select>
              )}
            />

            {isOtherOccupation() && (
              <Controller
                name={`accompanyList.${props.index}.otherOccupation`}
                control={control}
                render={({ field }) => <InputField type="text" value={field.value} onChange={field.onChange} />}
              />
            )}
          </div>

          {isEmployee(watchField.occupation) && (
            <div>
              <p css={guestInfoLabelStyle}>{t('Company name')}</p>
              <Controller
                name={`accompanyList.${props.index}.company`}
                control={control}
                render={({ field }) => (
                  <InputField type="text" value={field.value} onChange={field.onChange} placeholder={t('CUICIN Co Ltd')} />
                )}
              />
            </div>
          )}

          <div>
            <p css={guestInfoLabelStyle}>{t('E-mail delivery permission')}</p>
            <Controller
              name={`accompanyList.${props.index}.deliveryAgreementStatus`}
              control={control}
              render={({ field }) => (
                <Select value={field.value} onChange={field.onChange} customStyle={css({ paddingBottom: 0 })}>
                  <AntSelect.Option value="unconfirmed">{t('Unconfirmed')}</AntSelect.Option>
                  <AntSelect.Option value="deliverable">{t('Deliverable')}</AntSelect.Option>
                  <AntSelect.Option value="nonDeliverable">{t('Non deliverable')}</AntSelect.Option>
                </Select>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const formStyle = css({
  '.flex-input-block': { display: 'flex', gap: 16 },
  '.detail-form': {
    background: '#F2F2F2 0% 0% no-repeat padding-box',
    border: '1px solid #CCCCCC',
    borderRadius: 5,
    padding: 24,
    marginBottom: 16,
  },
  'input.mbsc-control': {
    backgroundColor: 'white',
  },
})

const guestInfoLabelStyle = css({
  marginBottom: 12,
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
})

const radioButtonWrapperStyle = css({ '& + &': { marginLeft: 40 } })
const radioButtonStyle = css({
  label: {
    color: '#272727',
    fontWeight: 'normal',
    fontSize: '14px',
    lineHeight: '20px',
    letterSpacing: '0.9px',
  },
})
